import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { LuLock } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Button } from "@/components/ui/button";
import { formatDecimalValue, formatUSD } from "@/utils/currency";

const BALANCE_DECIMAL_PLACES = 5;

interface WalletCurrencyRowProps {
  iconSrc: string;
  currencyName: string;
  chainName?: string;
  balance: number;
  usdValue: number;
  iconSize: number;
  renderBalanceValue: (
    content: (params: {
      desoExchangeRate: number;
      focusExchangeRate: number;
    }) => ReactNode,
  ) => ReactNode;
  renderUnlockValue?: () => ReactNode;
  showTopUpButton?: boolean;
  isLocked?: boolean;
  externalTrade?: boolean; // New prop
}

export const WalletCurrencyRow: React.FC<WalletCurrencyRowProps> = ({
  iconSrc,
  currencyName,
  chainName,
  balance,
  usdValue,
  iconSize,
  renderBalanceValue,
  renderUnlockValue,
  showTopUpButton = false,
  isLocked = false,
  externalTrade = false,
}) => {
  return (
    <div
      className="
        flex w-full items-center justify-between border-b border-border-light
        p-2
        last:border-none
      "
    >
      <div className="flex flex-row items-center gap-4">
        <div
          className="
            flex items-center gap-2 rounded-full text-sm text-foreground
          "
        >
          <Image
            width={iconSize}
            height={iconSize}
            src={iconSrc}
            alt={currencyName}
            className={`
              w-[calc( rounded-full
              ${iconSize}px
              / 2)]
              h-[calc(${iconSize}px
            `}
          />
        </div>
        <div className="flex flex-col">
          <div
            className="
              flex items-center font-mono text-sm text-foreground
              sm:text-sm
            "
          >
            {isLocked && <LuLock className="mr-1 text-lg" />}
            {currencyName}
          </div>
          {chainName && <div className="text-xs text-muted">{chainName}</div>}
        </div>
      </div>
      <div className="flex flex-row items-center gap-4">
        {externalTrade ? (
          <div className="flex flex-row items-center gap-2">
            <div
              className="
                flex gap-2 font-mono text-muted
                hover:text-foreground
              "
            >
              <span className="text-sm text-muted-foreground">
                {formatDecimalValue(
                  balance,
                  BALANCE_DECIMAL_PLACES,
                  BALANCE_DECIMAL_PLACES,
                )}{" "}
                <span
                  className="
                    hidden
                    sm:inline
                  "
                >
                  {currencyName}
                </span>
              </span>
              <InfoTooltip
                text={`Your Focus wallet will support ${currencyName} transactions soon, and is currently tradable via Openfund.`}
              />
            </div>
            <Link
              href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${currencyName.slice(1)}`}
              passHref
              target="_blank"
            >
              <Button variant="outline">
                Trade{" "}
                <span
                  className="
                    ml-1 hidden
                    sm:inline
                  "
                >
                  {currencyName}
                </span>
              </Button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col text-right">
            <div className="flex items-center text-xs text-muted">
              {renderBalanceValue(() => (
                <div
                  className="
                    flex flex-col font-mono text-muted
                    hover:text-foreground
                  "
                >
                  <span className="text-xs text-muted-foreground">
                    {formatDecimalValue(
                      balance,
                      BALANCE_DECIMAL_PLACES,
                      BALANCE_DECIMAL_PLACES,
                    )}{" "}
                    <span
                      className="
                        hidden
                        sm:inline
                      "
                    >
                      {currencyName}
                    </span>
                  </span>{" "}
                  <div>~{formatUSD(usdValue)} USD</div>
                </div>
              ))}
            </div>

            {renderUnlockValue?.()}
          </div>
        )}
        {showTopUpButton && <Button variant="outline">Top-Up</Button>}
      </div>
    </div>
  );
};
